import { useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { THE_BIEN_THAM_SO, chenTaiConTro } from './the-bien-tham-so'
import { useNavigate } from 'react-router-dom'
import { Upload, AlertTriangle, Plus, Trash2, Image as ImageIcon, X } from 'lucide-react'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea, Switch, Checkbox } from '@/components/ui/misc'
import { toast } from 'sonner'
import { api, apiError } from '@/lib/api-client'
import { currentLocale } from '@/lib/i18n'
import { useSegments } from '@/hooks/use-cdp'
import { RefreshCw } from 'lucide-react'
import {
  useTaoChienDichNhanTin, useNapNguoiNhan, useTranCung, useTaiKhoanBanBe, useTaiAnhChienDich, uocSoNgay,
  type NguonNguoiNhan, type TranCung, type BienTheNoiDung,
} from '@/hooks/use-zalo-broadcast-campaigns'

/** Giá trị phòng khi chưa tải được trần cứng từ máy chủ. */
const TRAN_MAC_DINH: TranCung = {
  maxDailyPerAccount: 200, maxDailyPerCampaign: 1000, maxVariants: 5, warmupLadder: [30, 50, 80, 100, 130, 160],
  minGapSeconds: 45, maxGapSeconds: 300, windowStartHour: 8, windowEndHour: 20, optOutFooter: '',
}

/** Đọc cột số điện thoại trong CSV (cột `phone` / `sdt` hoặc file một cột). */
export function docSoDienThoai(text: string): string[] {
  const dong = text.split(/\r?\n/).map((d) => d.trim()).filter(Boolean)
  if (dong.length === 0) return []
  const cot = dong[0].split(',').map((c) => c.trim().toLowerCase())
  const i = cot.findIndex((c) => c === 'phone' || c === 'sdt' || c.includes('điện thoại')) // i18n-ignore
  if (i < 0) return dong.map((d) => d.split(',')[0].trim()).filter((d) => /\d{8,}/.test(d))
  return dong.slice(1).map((d) => (d.split(',')[i] ?? '').trim()).filter(Boolean)
}

export function CreateBroadcastCampaignDialog({
  open, onOpenChange,
}: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const navigate = useNavigate()
  const { t } = useTranslation('marketing')
  const { data: taiKhoan = [] } = useTaiKhoanBanBe()
  const { data: tranApi } = useTranCung()
  const tran: TranCung = tranApi ?? TRAN_MAC_DINH
  const { data: segData, refetch: napLaiPhanKhuc, isFetching: dangNapPhanKhuc } = useSegments()
  const phanKhuc = segData?.segments ?? []
  const tao = useTaoChienDichNhanTin()
  const taiAnh = useTaiAnhChienDich()
  const fileRef = useRef<HTMLInputElement>(null)
  const anhRef = useRef<(HTMLInputElement | null)[]>([])
  const oTin = useRef<(HTMLTextAreaElement | null)[]>([])
  const oDangChon = useRef(0)
  const [dangTaiAnh, setDangTaiAnh] = useState<number | null>(null)

  const [ten, setTen] = useState('')
  // Nội dung mặc định GỬI THẲNG CHO KHÁCH — dữ liệu gửi khách, giữ tiếng Việt, người soạn tự sửa trước khi chạy.
  const [bienThe, setBienThe] = useState<BienTheNoiDung[]>([{ text: 'Chào {{ten}}, ' }]) // i18n-ignore
  const [chanTrang, setChanTrang] = useState(true)
  const [nguon, setNguon] = useState<NguonNguoiNhan>('all')
  const [segmentId, setSegmentId] = useState('')
  const [sdt, setSdt] = useState<string[]>([])
  const [tenFile, setTenFile] = useState('')
  const [chon, setChon] = useState<string[]>([])
  const [hanMuc, setHanMuc] = useState(tran.maxDailyPerAccount)
  const [hanMucTong, setHanMucTong] = useState(tran.maxDailyPerCampaign)
  const [warmup, setWarmup] = useState(true)
  const [gioBatDau, setGioBatDau] = useState(8)
  const [gioKetThuc, setGioKetThuc] = useState(20)
  const [dangLuu, setDangLuu] = useState(false)

  const tongBan = useMemo(
    () => taiKhoan.filter((a) => chon.includes(a.id)).reduce((s, a) => s + a.soBan, 0),
    [taiKhoan, chon],
  )
  const nguonOk =
    nguon === 'all' || (nguon === 'segment' && !!segmentId) || (nguon === 'csv' && sdt.length > 0)
  // Biến thể hợp lệ = có chữ HOẶC có ảnh (chỉ ảnh, không chữ vẫn gửi được — vd banner khuyến mãi).
  const tinHopLe: BienTheNoiDung[] = bienThe
    .map((v) => ({ text: v.text.trim(), imageUrl: v.imageUrl }))
    .filter((v) => v.text || v.imageUrl)
  const hopLe = ten.trim() && tinHopLe.length > 0 && chon.length > 0 && nguonOk
  const soNgay = uocSoNgay(
    nguon === 'csv' ? sdt.length : tongBan, chon.length, tran, hanMuc, hanMucTong,
  )
  // Cần bao nhiêu tài khoản để chạm trần cả chiến dịch.
  const canTk = Math.ceil(tran.maxDailyPerCampaign / tran.maxDailyPerAccount)

  /** Chèn thẻ biến vào ô tin đang chọn, tại vị trí con trỏ; con trỏ nằm ngay sau thẻ vừa chèn. */
  function chenBien(bien: string) {
    const i = Math.min(oDangChon.current, bienThe.length - 1)
    const el = oTin.current[i]
    const { noiDung, conTro } = chenTaiConTro(bienThe[i]?.text ?? '', bien, el?.selectionStart ?? null, el?.selectionEnd ?? null)
    setBienThe((s) => s.map((x, j) => (j === i ? { ...x, text: noiDung } : x)))
    requestAnimationFrame(() => { el?.focus(); el?.setSelectionRange(conTro, conTro) })
  }

  async function chonFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0]
    if (!f) return
    const ds = docSoDienThoai(await f.text())
    if (ds.length === 0) { toast.error(t('broadcastCreate.csvReadError')); return }
    setSdt(ds); setTenFile(f.name)
    toast.success(t('broadcastCreate.csvReadToast', { count: ds.length.toLocaleString(currentLocale()) }))
  }

  async function chonAnh(i: number, e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0]
    e.target.value = ''
    if (!f) return
    setDangTaiAnh(i)
    try {
      const url = await taiAnh.mutateAsync(f)
      setBienThe((s) => s.map((x, j) => (j === i ? { ...x, imageUrl: url } : x)))
    } catch (err) {
      toast.error(apiError(err))
    } finally {
      setDangTaiAnh(null)
    }
  }

  async function luu() {
    setDangLuu(true)
    try {
      const cd: any = await tao.mutateAsync({
        name: ten.trim(),
        source: nguon,
        segmentId: nguon === 'segment' ? segmentId : null,
        messages: tinHopLe,
        optOutFooter: chanTrang,
        dailyLimit: hanMuc,
        dailyTotalLimit: hanMucTong,
        warmupEnabled: warmup,
        sendWindowStart: gioBatDau,
        sendWindowEnd: gioKetThuc,
      })
      const { data: kq } = await api.post<{ them: number; boQua: number; tong: number }>(
        `/zalo-broadcast-campaigns/${cd.id}/targets/import`,
        { accountIds: chon, source: nguon, segmentId: nguon === 'segment' ? segmentId : undefined, phones: nguon === 'csv' ? sdt : undefined },
      )
      toast.success(t('broadcastCreate.createdToast', { imported: kq.them.toLocaleString(currentLocale()), skipped: kq.boQua }))
      onOpenChange(false)
      navigate(`/zalo-broadcast-campaigns/${cd.id}`)
    } catch (e) {
      toast.error(apiError(e))
    } finally {
      setDangLuu(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('broadcastCreate.title')}</DialogTitle>
          <DialogDescription>
            {t('broadcastCreate.description')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <div className="space-y-2">
            <Label>{t('broadcastCreate.nameLabel')}</Label>
            <Input value={ten} onChange={(e) => setTen(e.target.value)} placeholder={t('broadcastCreate.namePlaceholder')} />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>{t('broadcastCreate.contentLabel', { count: tinHopLe.length, max: tran.maxVariants })}</Label>
              {bienThe.length < tran.maxVariants && (
                <Button type="button" size="sm" variant="outline" onClick={() => setBienThe((s) => [...s, { text: '' }])}>
                  <Plus className="h-3 w-3" /> {t('broadcastCreate.addVariant')}
                </Button>
              )}
            </div>
            {bienThe.map((v, i) => (
              <div key={i} className="space-y-1.5 rounded-md border p-2">
                <div className="flex gap-2">
                  <Textarea rows={3} value={v.text} placeholder={t('broadcastCreate.variantPlaceholder', { n: i + 1 })}
                    ref={(el) => { oTin.current[i] = el }}
                    onFocus={() => { oDangChon.current = i }}
                    onChange={(e) => setBienThe((s) => s.map((x, j) => (j === i ? { ...x, text: e.target.value } : x)))} />
                  {bienThe.length > 1 && (
                    <Button type="button" size="icon" variant="ghost" onClick={() => setBienThe((s) => s.filter((_, j) => j !== i))}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                {v.imageUrl ? (
                  <div className="flex items-center gap-2">
                    <img src={v.imageUrl} alt="" className="h-14 w-14 rounded border object-cover" />
                    <Button type="button" size="sm" variant="outline"
                      onClick={() => setBienThe((s) => s.map((x, j) => (j === i ? { ...x, imageUrl: undefined } : x)))}>
                      <X className="h-3 w-3" /> {t('broadcastCreate.removeImage')}
                    </Button>
                  </div>
                ) : (
                  <Button type="button" size="sm" variant="outline" disabled={dangTaiAnh === i}
                    onClick={() => anhRef.current[i]?.click()}>
                    <ImageIcon className="h-3 w-3" /> {dangTaiAnh === i ? t('broadcastCreate.uploadingImage') : t('broadcastCreate.attachImage')}
                  </Button>
                )}
                <input ref={(el) => { anhRef.current[i] = el }} type="file" accept="image/jpeg,image/png,image/webp,image/gif"
                  className="hidden" onChange={(e) => chonAnh(i, e)} />
              </div>
            ))}
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-muted-foreground">{t('broadcastCreate.insertVarHint')}</span>
              {THE_BIEN_THAM_SO.map((vt) => (
                <button
                  key={vt.bien} type="button" title={t('broadcastCreate.varExample', { example: vt.vd })}
                  data-testid={`the-bien-${vt.bien.replace(/[{}]/g, '')}`}
                  className="rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-xs font-medium text-primary hover:bg-primary/20"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => chenBien(vt.bien)}
                >
                  + {vt.nhan}
                </button>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">
              {t('broadcastCreate.varHint')}
            </p>
            {tinHopLe.length === 1 && (
              <p className="flex items-center gap-1 text-xs text-amber-600">
                <AlertTriangle className="h-3 w-3" /> {t('broadcastCreate.singleVariantWarning')}
              </p>
            )}
          </div>

          <div className="flex items-start justify-between rounded-lg border p-3">
            <div className="pr-4">
              <Label>{t('broadcastCreate.optOutLabel')}</Label>
              <p className="text-xs text-muted-foreground">
                {t('broadcastCreate.optOutHint')}
              </p>
            </div>
            <Switch checked={chanTrang} onCheckedChange={setChanTrang} />
          </div>

          <div className="space-y-2">
            <Label>{t('broadcastCreate.accountsLabel', { count: chon.length, friends: tongBan.toLocaleString(currentLocale()) })}</Label>
            {taiKhoan.length === 0 ? (
              <p className="rounded-md border border-dashed p-3 text-sm text-muted-foreground">
                {t('broadcastCreate.noAccountsBefore')} <strong>{t('broadcastCreate.noAccountsIntegrations')}</strong> {t('broadcastCreate.noAccountsAfter')}
              </p>
            ) : (
              <div className="grid max-h-44 grid-cols-2 gap-2 overflow-y-auto rounded-md border p-2">
                {taiKhoan.map((a) => (
                  <label key={a.id} className="flex items-center gap-2 rounded px-2 py-1 text-sm hover:bg-muted">
                    <Checkbox
                      checked={chon.includes(a.id)}
                      onCheckedChange={(v) => setChon((s) => (v ? [...s, a.id] : s.filter((x) => x !== a.id)))}
                    />
                    <span className="truncate">{a.displayName ?? a.id.slice(0, 8)}</span>
                    <span className="ml-auto text-xs tabular-nums text-muted-foreground">{a.soBan.toLocaleString(currentLocale())}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label>{t('broadcastCreate.recipientsLabel')}</Label>
            <div className="flex flex-wrap gap-2">
              {([['all', t('broadcastCreate.sourceAll')], ['segment', t('broadcastCreate.sourceSegment')], ['csv', t('broadcastCreate.sourceCsv')]] as const).map(([v, nhan]) => (
                <Button key={v} type="button" size="sm" variant={nguon === v ? 'default' : 'outline'} onClick={() => setNguon(v)}>
                  {nhan}
                </Button>
              ))}
            </div>
            {nguon === 'segment' && phanKhuc.length === 0 && (
              <div className="space-y-2 rounded-md border border-dashed p-3 text-sm">
                <p>{t('broadcastCreate.noSegments')}</p>
                <div className="flex flex-wrap gap-2">
                  <Button asChild size="sm">
                    <a href="/customers/segments?tao=1" target="_blank" rel="noreferrer">
                      <Plus className="h-3 w-3" /> {t('broadcastCreate.createSegment')}
                    </a>
                  </Button>
                  <Button type="button" size="sm" variant="outline" disabled={dangNapPhanKhuc} onClick={() => void napLaiPhanKhuc()}>
                    <RefreshCw className={`h-3 w-3 ${dangNapPhanKhuc ? 'animate-spin' : ''}`} /> {t('broadcastCreate.reloadSegments')}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  {t('broadcastCreate.segmentFormStaysHint')}
                </p>
              </div>
            )}
            {nguon === 'segment' && phanKhuc.length > 0 && (
              <select
                className="h-9 w-full rounded-md border bg-background px-3 text-sm"
                value={segmentId} onChange={(e) => setSegmentId(e.target.value)}
              >
                <option value="">{t('broadcastCreate.selectSegment')}</option>
                {phanKhuc.map((s) => (
                  <option key={s.id} value={s.id}>{s.name} ({s.contactCount.toLocaleString(currentLocale())})</option>
                ))}
              </select>
            )}
            {nguon === 'csv' && (
              <div className="flex items-center gap-3">
                <Button type="button" variant="outline" onClick={() => fileRef.current?.click()}>
                  <Upload className="h-4 w-4" /> {t('broadcastCreate.chooseFile')}
                </Button>
                <span className="text-sm text-muted-foreground">
                  {tenFile ? t('broadcastCreate.fileSummary', { name: tenFile, count: sdt.length.toLocaleString(currentLocale()) }) : t('broadcastCreate.noFileChosen')}
                </span>
                <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={chonFile} />
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              {t('broadcastCreate.recipientsHintBefore')} <strong>{t('broadcastCreate.recipientsHintFriends')}</strong> {t('broadcastCreate.recipientsHintAfter')}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>{t('broadcastCreate.dailyPerAccountLabel')}</Label>
              <Input type="number" min={1} max={tran.maxDailyPerAccount} value={hanMuc}
                onChange={(e) => setHanMuc(Math.max(1, Math.min(tran.maxDailyPerAccount, Number(e.target.value) || 1)))} />
              <p className="text-xs text-muted-foreground">{t('broadcastCreate.dailyPerAccountHint', { max: tran.maxDailyPerAccount })}</p>
            </div>
            <div className="space-y-2">
              <Label>{t('broadcastCreate.dailyTotalLabel')}</Label>
              <Input type="number" min={1} max={tran.maxDailyPerCampaign} value={hanMucTong}
                onChange={(e) => setHanMucTong(Math.max(1, Math.min(tran.maxDailyPerCampaign, Number(e.target.value) || 1)))} />
              <p className="text-xs text-muted-foreground">
                {t('broadcastCreate.dailyTotalHint', { max: tran.maxDailyPerCampaign.toLocaleString(currentLocale()), accounts: canTk })}
              </p>
            </div>
            <div className="space-y-2">
              <Label>{t('broadcastCreate.windowStartLabel')}</Label>
              <Input type="number" min={0} max={23} value={gioBatDau}
                onChange={(e) => setGioBatDau(Math.max(0, Math.min(23, Number(e.target.value) || 0)))} />
            </div>
            <div className="space-y-2">
              <Label>{t('broadcastCreate.windowEndLabel')}</Label>
              <Input type="number" min={1} max={24} value={gioKetThuc}
                onChange={(e) => setGioKetThuc(Math.max(1, Math.min(24, Number(e.target.value) || 24)))} />
            </div>
          </div>

          <div className="flex items-center justify-between rounded-lg border p-3">
            <div>
              <Label>{t('broadcastCreate.warmupLabel')}</Label>
              <p className="text-xs text-muted-foreground">
                {t('broadcastCreate.warmupHint', { first: tran.warmupLadder[0], days: tran.warmupLadder.length })}
              </p>
            </div>
            <Switch checked={warmup} onCheckedChange={setWarmup} />
          </div>

          {chon.length > 0 && soNgay > 0 && (
            <p className="rounded-md bg-muted p-3 text-sm">
              {t('broadcastCreate.estimateBefore')} <strong>{t('broadcastCreate.estimateDays', { days: soNgay.toLocaleString(currentLocale()) })}</strong> {t('broadcastCreate.estimateAfter')}{' '}
              {t('broadcastCreate.gapHint', { min: tran.minGapSeconds, max: tran.maxGapSeconds })}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t('broadcastCreate.cancel')}</Button>
          <Button onClick={luu} disabled={!hopLe || dangLuu}>{dangLuu ? t('broadcastCreate.creating') : t('broadcastCreate.createButton')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
